import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity, sanitizePayload, formatImageUrl, cleanUrl, stripHtml } from '../../utils/security';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Save, ArrowLeft, Sparkles } from 'lucide-react';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const AdminPortfolioEdit = () => {
    const { id } = useParams();
    const { user, isSuperAdmin, loading: authLoading } = useAuth();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [usersList, setUsersList] = useState([]);
    const [categories, setCategories] = useState([]);
    
    // Default language based on active admin interface language
    const currentAdminLang = i18n.language === 'en' ? 'en' : 'ka';

    const [currentId, setCurrentId] = useState(id || null);
    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        category: 'web',
        summary: '',
        content: '',
        challenge: '',
        solution: '',
        results: '',
        live_url: '',
        image: '',
        offer_url: '',
        seo_title: '',
        seo_description: '',
        seo_keywords: '',
        lang: currentAdminLang,
        sort_order: 1,
        is_public: 1,
        assigned_user_id: ''
    });

    useEffect(() => {
        if (!authLoading && !isSuperAdmin()) navigate('/admin');
    }, [authLoading, isSuperAdmin, navigate]);

    // Fetch dynamic categories
    useEffect(() => {
        const fetchCats = async () => {
            try {
                const res = await fetchWithSecurity(`${API_URL}categories.php?type=portfolio`);
                const data = await res.json();
                if (Array.isArray(data) && data.length > 0) {
                    setCategories(data);
                }
            } catch (err) {
                console.error('Error fetching categories:', err);
            }
        };
        fetchCats();
    }, []);

    // Fetch project and users
    useEffect(() => {
        if (user) {
            const fetchUsersAndProject = async () => {
                try {
                    const usersRes = await fetchWithSecurity(`${API_URL}users.php`);
                    const usersData = await usersRes.json();
                    setUsersList(Array.isArray(usersData) ? usersData : []);

                    if (id) {
                        const response = await fetchWithSecurity(`${API_URL}portfolios.php?id=${id}`);
                        const data = await response.json();
                        if (data) {
                            // If current admin language is different from item's language, check if translated counterpart exists
                            if (data.lang !== currentAdminLang && data.slug) {
                                try {
                                    const matchRes = await fetchWithSecurity(`${API_URL}portfolios.php?slug=${data.slug}&lang=${currentAdminLang}`);
                                    const matchData = await matchRes.json();
                                    if (matchData && matchData.id) {
                                        // Load the counterpart matching admin language
                                        setCurrentId(matchData.id);
                                        setFormData({
                                            ...matchData,
                                            challenge: matchData.challenge || '',
                                            solution: matchData.solution || '',
                                            results: matchData.results || '',
                                            live_url: matchData.live_url || '',
                                            seo_title: matchData.seo_title || '',
                                            seo_description: matchData.seo_description || '',
                                            seo_keywords: matchData.seo_keywords || '',
                                            sort_order: matchData.sort_order !== undefined ? parseInt(matchData.sort_order) : 1,
                                            is_public: matchData.is_public !== undefined ? parseInt(matchData.is_public) : 1,
                                            assigned_user_id: matchData.assigned_user_id || '',
                                            lang: currentAdminLang
                                        });
                                        return;
                                    }
                                } catch (e) {
                                    // fallback to loading original data
                                }
                            }

                            setCurrentId(data.id);
                            setFormData({
                                ...data,
                                challenge: data.challenge || '',
                                solution: data.solution || '',
                                results: data.results || '',
                                live_url: data.live_url || '',
                                seo_title: data.seo_title || '',
                                seo_description: data.seo_description || '',
                                seo_keywords: data.seo_keywords || '',
                                sort_order: data.sort_order !== undefined ? parseInt(data.sort_order) : 1,
                                is_public: data.is_public !== undefined ? parseInt(data.is_public) : 1,
                                assigned_user_id: data.assigned_user_id || '',
                                lang: data.lang || currentAdminLang
                            });
                        }
                    } else {
                        // New project creation defaults to admin language
                        setFormData(prev => ({ ...prev, lang: currentAdminLang }));
                    }
                } catch (error) {
                    console.error('Error fetching data:', error);
                }
            };
            fetchUsersAndProject();
        }
    }, [id, user, currentAdminLang]);

    // Handle switching language between Georgian and English
    const handleSwitchLanguage = async (targetLang) => {
        if (formData.lang === targetLang) return;

        if (formData.slug) {
            setLoading(true);
            try {
                const res = await fetchWithSecurity(`${API_URL}portfolios.php?slug=${formData.slug}&lang=${targetLang}`);
                const match = await res.json();
                if (match && match.id) {
                    setCurrentId(match.id);
                    setFormData({
                        ...match,
                        challenge: match.challenge || '',
                        solution: match.solution || '',
                        results: match.results || '',
                        live_url: match.live_url || '',
                        seo_title: match.seo_title || '',
                        seo_description: match.seo_description || '',
                        seo_keywords: match.seo_keywords || '',
                        sort_order: match.sort_order !== undefined ? parseInt(match.sort_order) : 1,
                        is_public: match.is_public !== undefined ? parseInt(match.is_public) : 1,
                        assigned_user_id: match.assigned_user_id || '',
                        lang: targetLang
                    });
                    toast.info(targetLang === 'ka' ? 'გადართულია ქართულ ვერსიაზე' : 'Switched to English version');
                    setLoading(false);
                    return;
                }
            } catch (err) {
                console.error(err);
            }
            setLoading(false);
        }

        // Counterpart does not exist yet; switch editing lang to create counterpart
        setCurrentId(null);
        setFormData(prev => ({
            ...prev,
            lang: targetLang,
            title: '',
            summary: '',
            content: '',
            challenge: '',
            solution: '',
            results: ''
        }));
        toast.info(targetLang === 'ka' 
            ? 'ქართული ვერსია ჯერ არ არსებობს. შეგიძლიათ შეავსოთ და გამოაქვეყნოთ.' 
            : 'English version does not exist yet. You can fill out and publish it.'
        );
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : value 
        }));
    };

    const handleQuillChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const data = new FormData();
        data.append('image', file);
        try {
            const res = await fetchWithSecurity(`${API_URL}upload.php`, { method: 'POST', body: data });
            const result = await res.json();
            if (result.success) {
                setFormData(prev => ({ ...prev, image: cleanUrl(result.url) }));
                toast.success('სურათი აიტვირთა');
            } else {
                toast.error('სურათის ატვირთვა ვერ მოხერხდა: ' + result.error);
            }
        } catch (error) {
            toast.error('სურათის ატვირთვა ვერ მოხერხდა');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const sanitizedForm = sanitizePayload({
                ...formData,
                sort_order: parseInt(formData.sort_order) || 1,
                image: cleanUrl(formData.image),
                summary: stripHtml(formData.summary)
            });
            const response = await fetchWithSecurity(`${API_URL}portfolios.php`, {
                method: currentId ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(currentId ? { ...sanitizedForm, id: currentId } : sanitizedForm)
            });
            const result = await response.json();
            if (result.success) {
                toast.success('პროექტი წარმატებით შეინახა!');
                navigate('/admin/portfolio');
            } else {
                toast.error('შეცდომა: ' + (result.error || 'პროექტის შენახვა ვერ მოხერხდა'));
            }
        } catch (error) {
            toast.error('პროექტის შენახვა ვერ მოხერხდა');
        }
        setLoading(false);
    };

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link', 'image', 'code-block'],
            ['clean']
        ],
    };

    if (authLoading || !user) return null;

    const pageTitle = currentId ? 'პორტფოლიოს პროექტის რედაქტირება' : 'ახალი პორტფოლიო პროექტი';

    return (
        <AdminLayout title={pageTitle}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <button className="shadcn-btn shadcn-btn-secondary" onClick={() => navigate('/admin/portfolio')}>
                    <ArrowLeft size={16} /> {t('common.back') || 'პორტფოლიოში დაბრუნება'}
                </button>
                <button className="shadcn-btn shadcn-btn-primary" onClick={handleSubmit} disabled={loading}>
                    <Save size={16} /> {loading ? 'ინახება...' : 'პროექტის გამოქვეყნება'}
                </button>
            </div>

            <div className="shadcn-card max-w-4xl mx-auto">
                {/* ── PROMINENT LANGUAGE SWITCHER BAR WITH FLAGS ── */}
                <div className="d-flex align-items-center justify-content-between p-3 rounded-3 bg-light border mb-4 flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                        <span className="fw-bold small text-dark">
                            {formData.lang === 'ka' ? '🇬🇪 რედაქტირების ენა:' : '🇬🇧 Active Editing Language:'}
                        </span>
                        <span className="shadcn-badge shadcn-badge-primary">
                            {formData.lang === 'ka' ? '🇬🇪 ქართული (Georgian)' : '🇬🇧 ინგლისური (English)'}
                        </span>
                    </div>
                    <div className="d-flex gap-2">
                        <button
                            type="button"
                            className={`shadcn-btn shadcn-btn-sm ${formData.lang === 'ka' ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                            onClick={() => handleSwitchLanguage('ka')}
                        >
                            <span style={{ fontSize: '1.15rem', marginRight: '4px' }}>🇬🇪</span> ქართული ვერსია
                        </button>
                        <button
                            type="button"
                            className={`shadcn-btn shadcn-btn-sm ${formData.lang === 'en' ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                            onClick={() => handleSwitchLanguage('en')}
                        >
                            <span style={{ fontSize: '1.15rem', marginRight: '4px' }}>🇬🇧</span> English Version
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="row g-4">
                        {/* Language & Category */}
                        <div className="col-md-6">
                            <label className="form-group-label">{t('admin.portfolioEdit.language') || 'ენა'}</label>
                            <select 
                                name="lang" 
                                value={formData.lang} 
                                onChange={e => handleSwitchLanguage(e.target.value)} 
                                className="shadcn-select"
                            >
                                <option value="ka">🇬🇪 ქართული</option>
                                <option value="en">🇬🇧 English</option>
                            </select>
                        </div>
                        <div className="col-md-6">
                            <label className="form-group-label">{t('admin.portfolioEdit.category') || 'კატეგორია'}</label>
                            <select name="category" value={formData.category} onChange={handleInputChange} className="shadcn-select">
                                {categories.length > 0 ? (
                                    categories.map(cat => (
                                        <option key={cat.slug} value={cat.slug}>
                                            {formData.lang === 'ka' ? cat.name_ka : (cat.name_en || cat.name_ka)}
                                        </option>
                                    ))
                                ) : (
                                    <>
                                        <option value="web">ვებ დეველოპმენტი & სისტემები</option>
                                        <option value="app">მობილური აპლიკაცია</option>
                                        <option value="elearning">ელექტრონული სწავლება & ტექნოლოგიები</option>
                                        <option value="ai_dev">ხელოვნური ინტელექტი / პროგრამული უზრუნველყოფა</option>
                                    </>
                                )}
                            </select>
                        </div>

                        {/* Title & Slug */}
                        <div className="col-md-6">
                            <label className="form-group-label">{t('admin.portfolioEdit.title') || 'პროექტის დასახელება'}</label>
                            <input type="text" name="title" value={formData.title} onChange={handleInputChange} className="shadcn-input" required />
                        </div>
                        <div className="col-md-6">
                            <label className="form-group-label">{t('admin.portfolioEdit.slug') || 'URL იდენტიფიკატორი (Slug)'}</label>
                            <input type="text" name="slug" value={formData.slug} onChange={handleInputChange} className="shadcn-input" required />
                        </div>

                        {/* Visibility, Sort Order & Assigned User */}
                        <div className="col-md-4">
                            <label className="form-group-label">{t('admin.portfolioEdit.visibility') || 'ხილვადობა საჯარო გვერდზე'}</label>
                            <div className="d-flex align-items-center gap-2 mt-2">
                                <input 
                                    type="checkbox" 
                                    id="is_public" 
                                    name="is_public" 
                                    checked={formData.is_public === 1} 
                                    onChange={handleInputChange} 
                                    className="form-check-input mt-0 cursor-pointer"
                                    style={{ width: '20px', height: '20px' }}
                                />
                                <label htmlFor="is_public" className="small text-muted cursor-pointer mb-0">
                                    {t('admin.portfolioEdit.showPublic') || 'გამოჩნდეს პორტფოლიოს საჯარო სიაში'}
                                </label>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <label className="form-group-label">{t('admin.portfolioEdit.sortOrder') || 'პოზიცია / რიგი (Sort Order)'}</label>
                            <input 
                                type="number" 
                                name="sort_order" 
                                min="1"
                                value={formData.sort_order !== undefined ? formData.sort_order : 1} 
                                onChange={handleInputChange} 
                                className="shadcn-input" 
                                placeholder="1"
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-group-label">{t('admin.portfolioEdit.assignPartner') || 'მიბმული მომხმარებელი (პარტნიორი)'}</label>
                            <select name="assigned_user_id" value={formData.assigned_user_id} onChange={handleInputChange} className="shadcn-select">
                                <option value="">{t('admin.portfolioEdit.noUser') || '-- კონკრეტული მომხმარებელი არა --'}</option>
                                {usersList.map(u => (
                                    <option key={u.id} value={u.id}>{u.username} ({u.role})</option>
                                ))}
                            </select>
                        </div>

                        {/* Live URL & Offer URL */}
                        <div className="col-md-6">
                            <label className="form-group-label">{t('admin.portfolioEdit.liveUrl') || 'ლაივ ვებსაიტის ბმული'}</label>
                            <input type="url" name="live_url" value={formData.live_url} onChange={handleInputChange} placeholder="https://..." className="shadcn-input" />
                        </div>
                        <div className="col-md-6">
                            <label className="form-group-label">{t('admin.portfolioEdit.techOfferUrl') || 'ტექნიკური შეთავაზება (Offer PDF URL)'}</label>
                            <input type="text" name="offer_url" value={formData.offer_url} onChange={handleInputChange} placeholder="PDF ბმული ან ფაილის მისამართი" className="shadcn-input" />
                        </div>

                        {/* Image Upload */}
                        <div className="col-md-12">
                            <label className="form-group-label">{t('admin.portfolioEdit.thumbnail') || 'პროექტის ქავერ სურათი'}</label>
                            <div className="d-flex gap-4 align-items-center p-3 rounded-3 bg-light border">
                                {formData.image && (
                                    <img src={formatImageUrl(formData.image)} alt="Preview" className="rounded-3 object-fit-cover shadow-sm" style={{ width: '100px', height: '70px' }} />
                                )}
                                <div className="flex-grow-1">
                                    <input type="file" onChange={handleImageUpload} className="form-control mb-2" accept="image/*" />
                                    <input type="text" name="image" value={formData.image} onChange={handleInputChange} placeholder="ან ჩასვით სურათის URL" className="shadcn-input" />
                                </div>
                            </div>
                        </div>

                        {/* Short Summary */}
                        <div className="col-md-12">
                            <label className="form-group-label">{t('admin.portfolioEdit.shortSummary') || 'მოკლე მიმოხილვა'}</label>
                            <textarea name="summary" value={formData.summary} onChange={handleInputChange} rows="3" className="shadcn-input" placeholder="მოკლე ტექსტი ბარათისთვის..."></textarea>
                        </div>

                        {/* Rich Content */}
                        <div className="col-md-12">
                            <label className="form-group-label">სრული აღწერა</label>
                            <div className="quill-wrapper">
                                <ReactQuill value={formData.content} onChange={(val) => handleQuillChange('content', val)} modules={quillModules} className="bg-white rounded-3 overflow-hidden border" />
                            </div>
                        </div>

                        {/* Case Study Details */}
                        <div className="col-md-12">
                            <h5 className="fw-bold text-primary mt-3 mb-1">ქეის სტადის დეტალები</h5>
                            <p className="text-muted small mb-3">აღწერეთ პროექტის გამოწვევა, მიღებული გადაწყვეტა და შედეგები.</p>
                        </div>

                        <div className="col-md-12">
                            <label className="form-group-label">{t('admin.portfolioEdit.caseChallenge') || 'გამოწვევა'}</label>
                            <div className="quill-wrapper">
                                <ReactQuill value={formData.challenge} onChange={(val) => handleQuillChange('challenge', val)} modules={quillModules} className="bg-white rounded-3 overflow-hidden border" />
                            </div>
                        </div>

                        <div className="col-md-12">
                            <label className="form-group-label">{t('admin.portfolioEdit.caseSolution') || 'გადაწყვეტა'}</label>
                            <div className="quill-wrapper">
                                <ReactQuill value={formData.solution} onChange={(val) => handleQuillChange('solution', val)} modules={quillModules} className="bg-white rounded-3 overflow-hidden border" />
                            </div>
                        </div>

                        <div className="col-md-12">
                            <label className="form-group-label">{t('admin.portfolioEdit.caseResults') || 'შედეგები'}</label>
                            <div className="quill-wrapper">
                                <ReactQuill value={formData.results} onChange={(val) => handleQuillChange('results', val)} modules={quillModules} className="bg-white rounded-3 overflow-hidden border" />
                            </div>
                        </div>

                        {/* SEO Section */}
                        <div className="col-md-12">
                            <h5 className="fw-bold text-dark mt-4 mb-3 d-flex align-items-center gap-2">
                                <Sparkles size={18} className="text-primary" /> {t('admin.portfolioEdit.seoSettings') || 'საძიებო ოპტიმიზაცია (SEO)'}
                            </h5>
                        </div>

                        <div className="col-md-12">
                            <label className="form-group-label">{t('admin.portfolioEdit.seoTitle') || 'SEO სათაური'}</label>
                            <input type="text" name="seo_title" value={formData.seo_title} onChange={handleInputChange} placeholder="Meta Title (მაქს. 60 სიმბოლო)" className="shadcn-input" />
                        </div>

                        <div className="col-md-12">
                            <label className="form-group-label">{t('admin.portfolioEdit.seoDescription') || 'SEO აღწერა'}</label>
                            <textarea name="seo_description" value={formData.seo_description} onChange={handleInputChange} rows="2" placeholder="Meta Description (მაქს. 160 სიმბოლო)" className="shadcn-input"></textarea>
                        </div>

                        <div className="col-md-12">
                            <label className="form-group-label">{t('admin.portfolioEdit.seoKeywords') || 'საკვანძო სიტყვები'}</label>
                            <input type="text" name="seo_keywords" value={formData.seo_keywords} onChange={handleInputChange} placeholder="მაგ. web development, react, e-commerce" className="shadcn-input" />
                        </div>
                    </div>

                    <div className="mt-4 pt-3 border-top d-flex justify-content-end gap-2">
                        <button type="button" className="shadcn-btn shadcn-btn-secondary" onClick={() => navigate('/admin/portfolio')}>
                            {t('common.cancel') || 'გაუქმება'}
                        </button>
                        <button type="submit" className="shadcn-btn shadcn-btn-primary" disabled={loading}>
                            <Save size={16} /> {loading ? 'ინახება...' : 'პროექტის გამოქვეყნება'}
                        </button>
                    </div>
                </form>
            </div>
        </AdminLayout>
    );
};

export default AdminPortfolioEdit;
